/**
 * Widget Structure Mapping Utilities
 * 
 * This module provides utilities for recursively processing widget configurations
 * and mapping them to their corresponding components, formatters, and dimensions.
 */

/**
 * Recursively maps widget structures and enriches them with component metadata
 * 
 * This function traverses any level of nested widget arrays and adds the necessary
 * component properties (formatter, widgetComponent, dimensions) to each widget
 * based on the widget master configuration.
 * 
 * @param structure - The widget structure (can be nested arrays or individual widgets)
 * @param widgetMaster - Array of widget configurations containing:
 *   - id: Widget type identifier (e.g., WidgetType.W1)
 *   - widget: React component to render
 *   - formatter: Function to format widget data
 *   - dimensions: Array of supported dimensions
 * 
 * @returns The enriched widget structure with added component metadata
 * 
 * @example
 * // Input: Nested widget structure
 * const structure = [
 *   [
 *     { type: "W1", identifier: "widget1" },
 *     { type: "W2", identifier: "widget2" }
 *   ]
 * ];
 * 
 * // Output: Enriched with component metadata
 * const enriched = mapWidgetStructure(structure, widgetMaster);
 * // Result: Each widget now has formatter, widgetComponent, and dimensions properties
 */
export const mapWidgetStructure = (structure: any, widgetMaster: any): any => {
    if (Array.isArray(structure)) {
        return structure.map((item: any) => mapWidgetStructure(item, widgetMaster));
    } else if (structure && typeof structure === 'object' && structure.type) {
        // This is an actual widget object
        const widgetType = widgetMaster.find(
            (w: any) => w.id === structure.type
        );
        return {
            ...structure,
            formatter: widgetType?.formatter || null,
            widgetComponent: widgetType?.widget || null,
            dimensions: widgetType?.dimensions || [],
        };
    }
    return structure;
};

/**
 * Generic recursive widget renderer utility that can handle any level of widget nesting
 * @param structure - The widget structure (can be nested arrays or individual widgets)
 * @param renderWidget - Function to render an individual widget
 * @param keyPrefix - Key prefix for React keys
 * @returns Rendered widget structure
 */
export const renderWidgetStructure = (
    structure: any, 
    renderWidget: (widget: any, index: number, keyPrefix?: string) => React.ReactNode,
    keyPrefix: string = ""
): React.ReactNode => {
    if (Array.isArray(structure)) {
        // Check if all elements are widgets (objects with type property)
        const allAreWidgets = structure.every(item => 
            item && typeof item === 'object' && item.type && !Array.isArray(item)
        );
        
        if (allAreWidgets) {
            // This is a group of widgets - render them directly
            return structure.map((widget, widgetIndex) =>
                renderWidget(widget, widgetIndex, `${keyPrefix}-${widgetIndex}`)
            );
        } else {
            // This is a nested structure - recurse further
            return structure.map((item, index) =>
                renderWidgetStructure(item, renderWidget, `${keyPrefix}-${index}`)
            );
        }
    } else if (structure && typeof structure === 'object' && structure.type) {
        // This is a single widget
        return renderWidget(structure, 0, keyPrefix);
    }
    return null;
};

/**
 * Flattens widget structure to get all widgets regardless of nesting level
 * @param structure - The widget structure
 * @returns Array of all widgets
 */
export const flattenWidgetStructure = (structure: any): any[] => {
    const widgets: any[] = [];
    
    const traverse = (item: any) => {
        if (Array.isArray(item)) {
            item.forEach(traverse);
        } else if (item && typeof item === 'object' && item.type) {
            widgets.push(item);
        }
    };
    
    traverse(structure);
    return widgets;
}; 